import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { Box } from '@theme-ui/components';
import pick from 'lodash.pick';
import { css } from '@emotion/react';

import { AlertType } from '@app/components/Alert';
import { Button, getButtonStyles } from '@app/components/Button';
import ErrorBoundary from '@app/components/ErrorBoundary';
import Link from '@app/components/Link';
import Loader from '@app/components/Loader';
import { H2, H3 } from '@app/components/typography';
import useGTMTracking from '@app/hooks/useGTMTracking';
import type { AppTheme } from '@app/styles/theme';
import theme from '@app/styles/theme';

import type { ReturnRequestAlertType } from '../ReturnRequest/components/ReturnRequestAlerts';
import { ReturnRequestAlerts } from '../ReturnRequest/components/ReturnRequestAlerts';
import { useReturnRequestModal } from '../ReturnRequest/hooks/useReturnRequestModal';
import { useShowReturnHandler } from '../ReturnRequest/hooks/useShowReturnHandler';
import ItemsDetail from './ItemsDetail';
import messages from './OrderHistoryDetail.messages';
import {
  OrderHistoryDetailProvider,
  useOrderHistoryDetail,
} from './OrderHistoryDetailContext';
import PaymentDetail from './PaymentDetail';
import Summary from './Summary';

const SUPPORT_URL =
  'https://support.native-instruments.com/hc/en-us/categories/360000053657';

const OrderHistoryDetailContent = () => {
  const { itemsData, itemsQueryState, paymentQueryState, contactEmail } =
    useOrderHistoryDetail();
  const { openReturnModal } = useReturnRequestModal();
  const [returnAlert, setReturnAlert] = useState<ReturnRequestAlertType>(null);

  const { showReturnHandler } = useShowReturnHandler(
    pick(itemsData, ['orderDate', 'statusCode', 'items']),
  );
  const areAllProductsSubmitted = itemsData?.items?.every(
    (product) =>
      product && 'returnStatus' in product && product.returnStatus !== null,
  );

  const showReturnButton = showReturnHandler && !areAllProductsSubmitted;

  useGTMTracking({});

  function refetch() {
    itemsQueryState.refetch();
    paymentQueryState.refetch();
  }

  function handleReturnRequestSuccess() {
    refetch();
    setReturnAlert(AlertType.Success);
  }

  function handleReturnRequestError() {
    setReturnAlert(AlertType.Error);
  }

  function openReturnRequestModal() {
    if (!itemsData) return;
    openReturnModal({
      orderDate: itemsData.orderDate,
      orderUid: itemsData.id,
      orderProducts: itemsData.items,
      contactEmail: contactEmail || '',
      onSuccess: handleReturnRequestSuccess,
      onError: handleReturnRequestError,
    });
  }
  if (itemsQueryState.loading || paymentQueryState.loading) {
    return <Loader />;
  }

  return (
    <Box
      css={css`
        font-size: ${theme.fontSizes[2]};
        max-width: ${theme.layout.containerLargeWidth};
        margin: 0 auto;
      `}
    >
      <ReturnRequestAlerts alert={returnAlert} />
      <Box mb={20}>
        <H2 as="h1">
          <FormattedMessage {...messages.title} />
        </H2>

        <H3>
          <FormattedMessage {...messages.subTitle} /> #
          <span data-testid="main-order-number">{itemsData?.number}</span>
        </H3>
        <Summary />
      </Box>
      <a
        href={SUPPORT_URL}
        css={(theme: AppTheme) => getButtonStyles({ type: 'primary', theme })}
        data-testid="support-url"
      >
        <FormattedMessage {...messages.supportButton} />
      </a>

      {showReturnButton && (
        <Box ml={16} sx={{ display: 'inline-block' }}>
          <Button
            type="primary"
            onClick={openReturnRequestModal}
            data-testid="return-request-button"
          >
            <FormattedMessage {...messages.returnRequestButton} />
          </Button>
        </Box>
      )}

      <Box mb={40} mt={40}>
        <ItemsDetail />
      </Box>

      <Box mb={40}>
        <PaymentDetail />
      </Box>

      <Link
        to="/:language/account/orders/"
        css={(theme: AppTheme) => getButtonStyles({ type: 'secondary', theme })}
      >
        <span data-testid="back-to-orders">
          <FormattedMessage {...messages.backButton} />
        </span>
      </Link>
    </Box>
  );
};

const OrderHistoryDetailWrapper = () => {
  /*
  Translated strings from Magento2 (eg country name, status) are localised according to the current Store code header, so we need to ensure it is set before proceeding
  */
  const { orderNumber } = useParams<{ orderNumber?: string }>();

  if (!orderNumber) return null;

  return (
    <OrderHistoryDetailProvider orderNumber={orderNumber}>
      <OrderHistoryDetailContent />
    </OrderHistoryDetailProvider>
  );
};

const OrderHistoryDetail = () => (
  <ErrorBoundary>
    <OrderHistoryDetailWrapper />
  </ErrorBoundary>
);

export default OrderHistoryDetail;
